import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { Routes, Route } from "react-router-dom";
import "./App.css";
import Header from "./components/Header";
import CBCTGeomCalculator from "./pages/CBCTGeomCalculator";
import GeometrySelector, {
  GEOMETRY_TYPES,
} from "./components/GeometrySelector";
import GeometryForm from "./components/GeometryForm";
import Viewer3D from "./components/Viewer3D";
import Accordion from "./components/Accordion";
import { fetchVectors } from "./services/vectorApi";

// Default parameters
const DEFAULT_PARAMS = {
  sod: 1, // Source-to-object distance (mm)
  odd: 2, // Object-to-detector distance (mm)
  detectorWidth: 512, // Detector width (px)
  detectorHeight: 512, // Detector height (px)
  offsetX: 0, // Offset-X (px)
  offsetY: 0, // Offset-Y (px)
  pitch: 0.2, // Pitch (mm)
  numScans: 5, // Number of scans
  startAngle: 0, // Start angle (deg)
  endAngle: 360, // End angle (deg)
  tilt: 55, // Tilt angle (deg)
  fovCenterAsOrigin: false, // FOV center as origin (for perpendicular method)
  // Dummy object default parameters
  showDummy: false,
  dummyType: 'plane',
  dummyColor: '#64b5e7',
  dummyPlane: { width: 25, height: 25, thickness: 0.15 },
  dummyCylinder: { radius: 2, height: 5 },
};


// Sidebar constraints
const SIDEBAR_MAX_WIDTH = 420 + 24 * 2;
const SIDEBAR_MIN_WIDTH = 0;

function MainVisualization() {
  const [geometryType, setGeometryType] = useState(GEOMETRY_TYPES.TOMOGRAPHY);
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [vectors, setVectors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Display options
  const [showFloor, setShowFloor] = useState(false);
  const [showAxisX, setShowAxisX] = useState(true);
  const [showAxisY, setShowAxisY] = useState(true);
  const [showAxisZ, setShowAxisZ] = useState(true);

  // Arrow visibility controls
  const [showArrows, setShowArrows] = useState(true); // Master toggle for all arrows
  const [showUVector, setShowUVector] = useState(true);
  const [showVVector, setShowVVector] = useState(true);
  const [showDirectionToSource, setShowDirectionToSource] = useState(true);
  const [showDirectionToDetector, setShowDirectionToDetector] = useState(true);

  // Dummy object state
  const [showDummy, setShowDummy] = useState(DEFAULT_PARAMS.showDummy);
  const [dummyType, setDummyType] = useState(DEFAULT_PARAMS.dummyType);
  const [dummyColor, setDummyColor] = useState(DEFAULT_PARAMS.dummyColor);
  const [dummyPlane, setDummyPlane] = useState(DEFAULT_PARAMS.dummyPlane);
  const [dummyCylinder, setDummyCylinder] = useState(DEFAULT_PARAMS.dummyCylinder);

  // Animation state
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentProjectionIndex, setCurrentProjectionIndex] = useState(0);
  const [animationSpeed, setAnimationSpeed] = useState(300);
  const [timeUnit, setTimeUnit] = useState("ms"); // 'ms' or 's'
  const animationIntervalRef = useRef(null);

  // Sidebar resize state
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_MAX_WIDTH);
  const isResizing = useRef(false);

  const handleMouseDown = useCallback((e) => {
    isResizing.current = true;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing.current) return;
    const newWidth = e.clientX;
    if (newWidth >= SIDEBAR_MIN_WIDTH && newWidth <= SIDEBAR_MAX_WIDTH) {
      setSidebarWidth(newWidth);
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    isResizing.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, [handleMouseMove]);

  // Animation control handlers
  const handleStartStop = useCallback(() => {
    if (isAnimating) {
      // Stop simulation
      setIsAnimating(false);
      setIsPlaying(false);
      setCurrentProjectionIndex(0);
    } else {
      // Start simulation
      setIsAnimating(true);
      setIsPlaying(true);
    }
  }, [isAnimating]);

  const handlePlayPause = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  const handleRestart = useCallback(() => {
    setCurrentProjectionIndex(0);
    setIsPlaying(true);
  }, []);

  const handleSpeedChange = useCallback((e) => {
    const value = parseFloat(e.target.value);
    if (value > 0) {
      setAnimationSpeed(value);
    }
  }, []);

  const handleTimeUnitChange = useCallback((e) => {
    setTimeUnit(e.target.value);
  }, []);

  const handleResetParams = useCallback(() => {
    setParams(DEFAULT_PARAMS);
    setGeometryType(GEOMETRY_TYPES.TOMOGRAPHY);
    // Reset dummy object properties
    setShowDummy(DEFAULT_PARAMS.showDummy);
    setDummyType(DEFAULT_PARAMS.dummyType);
    setDummyColor(DEFAULT_PARAMS.dummyColor);
    setDummyPlane(DEFAULT_PARAMS.dummyPlane);
    setDummyCylinder(DEFAULT_PARAMS.dummyCylinder);
  }, []);

  // Fetch vectors from PHP API whenever params or geometry type changes
  useEffect(() => {
    const {
      sod,
      odd,
      pitch,
      numScans,
    } = params;

    // Validate parameters before fetching
    if (sod <= 0 || odd <= sod || pitch <= 0 || numScans < 1) {
      setVectors([]);
      return;
    }

    // Use 90° tilt for tomography
    const tilt = geometryType === GEOMETRY_TYPES.TOMOGRAPHY ? 90 : params.tilt;

    setIsLoading(true);
    setError(null);

    fetchVectors({
      geometryType,
      numScans: params.numScans,
      startAngle: params.startAngle,
      endAngle: params.endAngle,
      tilt,
      sod: params.sod,
      odd: params.odd,
      pitch: params.pitch,
      offsetX: params.offsetX,
      offsetY: params.offsetY,
      fovCenterAsOrigin: params.fovCenterAsOrigin,
    })
      .then((data) => {
        setVectors(data.vectors);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch vectors:', err);
        setError(err.message);
        setVectors([]);
        setIsLoading(false);
      });
  }, [geometryType, params]);

  // Calculate detector dimensions in visualization units
  const detectorVisSize = useMemo(() => {
    const scale = 0.02; // Scale factor for visualization
    return {
      width: params.detectorWidth * params.pitch * scale,
      height: params.detectorHeight * params.pitch * scale,
    };
  }, [params.detectorWidth, params.detectorHeight, params.pitch]);

  // Playback navigation handlers (defined after vectors)
  const handleNext = useCallback(() => {
    setCurrentProjectionIndex((prev) => (prev + 1) % vectors.length);
  }, [vectors.length]);

  const handleBack = useCallback(() => {
    setCurrentProjectionIndex(
      (prev) => (prev - 1 + vectors.length) % vectors.length
    );
  }, [vectors.length]);

  // Animation effect
  useEffect(() => {
    if (isAnimating && isPlaying && vectors.length > 0) {
      const intervalMs =
        timeUnit === "s" ? animationSpeed * 1000 : animationSpeed;

      animationIntervalRef.current = setInterval(() => {
        setCurrentProjectionIndex((prev) => (prev + 1) % vectors.length);
      }, intervalMs);

      return () => {
        if (animationIntervalRef.current) {
          clearInterval(animationIntervalRef.current);
        }
      };
    } else {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
      }
    }
  }, [isAnimating, isPlaying, vectors.length, animationSpeed, timeUnit]);

  // Reset animation index when vectors change
  useEffect(() => {
    setCurrentProjectionIndex(0);
    setIsAnimating(false);
    setIsPlaying(false);
  }, [vectors]);

  return (
    <div className="app">
      <Header />

      <main className="main-content">
        <aside
          className="sidebar"
          style={{ width: sidebarWidth, minWidth: SIDEBAR_MIN_WIDTH }}
        >
          <div className="sidebar-content">
            <GeometrySelector
              value={geometryType}
              onChange={setGeometryType}
              params={params}
              onParamsChange={setParams}
            />

            <GeometryForm params={params} onChange={setParams} geometryType={geometryType} />
            <div className="form-section">
              <Accordion title="Display" defaultOpen={true}>
                <div
                  style={{
                    fontSize: "0.8rem",
                    opacity: 0.7,
                    marginBottom: "8px",
                  }}
                >
                  {isLoading ? (
                    "Loading vectors..."
                  ) : error ? (
                    <span style={{ color: "#f87171" }}>Error: {error}</span>
                  ) : (
                    <>
                      Projections: {vectors.length}
                      {isAnimating && ` (Current: ${currentProjectionIndex + 1})`}
                    </>
                  )}
                </div>

                {/* Animation Controls */}
                <div className="animation-controls">
                  <button
                    className={`btn-animation ${isAnimating ? "playing" : ""}`}
                    onClick={handleStartStop}
                    disabled={vectors.length === 0 || isLoading}
                  >
                    {isAnimating ? "Stop" : "Start"} Simulation
                  </button>

                  {isAnimating && (
                    <>
                      <div className="playback-controls">
                        <button
                          className="playback-btn"
                          onClick={handleBack}
                          title="Previous Projection"
                        >
                          ◀
                        </button>
                        <button
                          className="playback-btn"
                          onClick={handleRestart}
                          title="Restart"
                        >
                          ⟲
                        </button>
                        <button
                          className="playback-btn playback-btn-large"
                          onClick={handlePlayPause}
                          title={isPlaying ? "Pause" : "Play"}
                        >
                          {isPlaying ? "❚❚" : "▶"}
                        </button>
                        <button
                          className="playback-btn"
                          onClick={handleNext}
                          title="Next Projection"
                        >
                          ▶
                        </button>
                      </div>

                      <div className="animation-speed-controls">
                        <div
                          className="form-group"
                          style={{ marginBottom: 0, flex: 1 }}
                        >
                          <label>Speed</label>
                          <input
                            type="number"
                            min="0.1"
                            step="0.1"
                            value={animationSpeed}
                            onChange={handleSpeedChange}
                          />
                        </div>
                        <div
                          className="form-group"
                          style={{ marginBottom: 0, width: "80px" }}
                        >
                          {/* <label>Unit</label> */}
                          <select value={timeUnit} onChange={handleTimeUnitChange}>
                            <option value="ms">ms</option>
                            <option value="s">s</option>
                          </select>
                        </div>
                      </div>
                    </>
                  )}
                </div>


                {/* Dummy Object Controls */}
                <div className="dummy-controls">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={showDummy}
                      onChange={(e) => setShowDummy(e.target.checked)}
                    />
                    Dummy
                  </label>

                  {showDummy && (
                    <div className="dummy-options">
                      <div className="form-group" style={{ marginBottom: "8px" }}>
                        <label>Object</label>
                        <select
                          value={dummyType}
                          onChange={(e) => setDummyType(e.target.value)}
                        >
                          <option value="cylinder">Cylinder</option>
                          <option value="plane">Plane</option>
                        </select>
                      </div>

                      <div className="form-group" style={{ marginBottom: "8px" }}>
                        <label>Color</label>
                        <input
                          type="color"
                          value={dummyColor}
                          onChange={(e) => setDummyColor(e.target.value)}
                          style={{ width: "100%", height: "32px", padding: "2px" }}
                        />
                      </div>

                      {dummyType === "cylinder" ? (
                        <>
                          <div className="form-group" style={{ marginBottom: "8px" }}>
                            <label>Radius</label>
                            <input
                              type="number"
                              min="0.01"
                              step="any"
                              value={dummyCylinder.radius}
                              onChange={(e) =>
                                setDummyCylinder({
                                  ...dummyCylinder,
                                  radius: parseFloat(e.target.value) || 0.1,
                                })
                              }
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: "8px" }}>
                            <label>Height</label>
                            <input
                              type="number"
                              min="0.01"
                              step="any"
                              value={dummyCylinder.height}
                              onChange={(e) =>
                                setDummyCylinder({
                                  ...dummyCylinder,
                                  height: parseFloat(e.target.value) || 0.1,
                                })
                              }
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="form-group" style={{ marginBottom: "8px" }}>
                            <label>Width</label>
                            <input
                              type="number"
                              min="0.01"
                              step="any"
                              value={dummyPlane.width}
                              onChange={(e) =>
                                setDummyPlane({
                                  ...dummyPlane,
                                  width: parseFloat(e.target.value) || 0.1,
                                })
                              }
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: "8px" }}>
                            <label>Height</label>
                            <input
                              type="number"
                              min="0.01"
                              step="any"
                              value={dummyPlane.height}
                              onChange={(e) =>
                                setDummyPlane({
                                  ...dummyPlane,
                                  height: parseFloat(e.target.value) || 0.1,
                                })
                              }
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: "8px" }}>
                            <label>Thickness</label>
                            <input
                              type="number"
                              min="0.01"
                              step="0.01"
                              value={dummyPlane.thickness}
                              onChange={(e) =>
                                setDummyPlane({
                                  ...dummyPlane,
                                  thickness: parseFloat(e.target.value) || 0.01,
                                })
                              }
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div className="reset-params-container">
                  <button
                    className="reset-params-btn"
                    onClick={handleResetParams}
                    title="Reset to Default Parameters">
                    Reset Parameters
                  </button>
                </div>
              </Accordion>
            </div>
          </div>
        </aside>
        <div className="sidebar-resizer" onMouseDown={handleMouseDown} />

        <div className="viewer-container">
          <Viewer3D
            vectors={vectors}
            detectorWidth={detectorVisSize.width}
            detectorHeight={detectorVisSize.height}
            showFloor={showFloor}
            setShowFloor={setShowFloor}
            showAxisX={showAxisX}
            setShowAxisX={setShowAxisX}
            showAxisY={showAxisY}
            setShowAxisY={setShowAxisY}
            showAxisZ={showAxisZ}
            setShowAxisZ={setShowAxisZ}
            isAnimating={isAnimating}
            currentProjectionIndex={currentProjectionIndex}
            showDummy={showDummy}
            dummyType={dummyType}
            dummyColor={dummyColor}
            dummyCylinder={dummyCylinder}
            dummyPlane={dummyPlane}
            showArrows={showArrows}
            setShowArrows={setShowArrows}
            showUVector={showUVector}
            setShowUVector={setShowUVector}
            showVVector={showVVector}
            setShowVVector={setShowVVector}
            showDirectionToSource={showDirectionToSource}
            setShowDirectionToSource={setShowDirectionToSource}
            showDirectionToDetector={showDirectionToDetector}
            setShowDirectionToDetector={setShowDirectionToDetector}
          />
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/cbct-vol-geom-calculator" element={<CBCTGeomCalculator />} />
      <Route path="/*" element={<MainVisualization />} />
    </Routes>
  );
}

export default App;
